import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Typography, 
  Steps, 
  Button,
  Form,
  Input,
  Checkbox,
  Divider,
  Space,
  Alert,
  Modal,
  Tag
} from 'antd';
import { Header } from '../../components/common/layout';
import { 
  CheckCircleOutlined,
  CreditCardOutlined,
  CheckOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { AuthModal } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const SignupPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    // Check for referral code in URL
    const refCode = searchParams.get('ref');
    if (refCode) {
      console.log('Referral code detected:', refCode);
    }
    
    // If user is not authenticated and there's a referral code, show auth modal
    if (!currentUser && refCode) {
      setAuthModalVisible(true);
    }
  }, [searchParams, currentUser]);

  const membershipPlans = [
    {
      id: 'basic',
      name: 'Basic Care',
      price: '$49-79',
      period: '/month',
      features: [
        'Lawn mowing (monthly)',
        'Leaf blowing (seasonal)',
        'Member pricing on gutter cleaning',
        'Basic support'
      ],
      popular: false
    },
    {
      id: 'standard',
      name: 'Standard Care',
      price: '$99-149',
      period: '/month',
      features: [
        'Everything in Basic Care',
        'Window cleaning (quarterly)',
        'Trash can cleaning (monthly)',
        '10% off Add-On Services',
        'Priority support'
      ],
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium Care',
      price: '$199-249',
      period: '/month',
      features: [
        'Everything in Standard Care',
        'Priority scheduling',
        '1 Bonus Service Credit per quarter',
        '20% off Add-On Services',
        'VIP support',
        'Dedicated account manager'
      ],
      popular: false
    },
    {
      id: 'landlord',
      name: 'Landlord Package',
      price: '$79',
      period: '/unit/month',
      features: [
        'Lawn maintenance',
        'Trash can cleaning',
        'Seasonal gutter cleaning',
        'Multi-property management',
        'Bulk scheduling discounts'
      ],
      popular: false,
      special: true
    }
  ];

  const addOnServices = [
    { id: 'roof-cleaning', name: 'Roof Cleaning', price: 300, category: 'Exterior' },
    { id: 'deck-cleaning', name: 'Deck Cleaning', price: 250, category: 'Exterior' },
    { id: 'patio-cleaning', name: 'Patio Cleaning', price: 250, category: 'Exterior' },
    { id: 'driveway-cleaning', name: 'Driveway Cleaning', price: 300, category: 'Exterior' },
    { id: 'fence-cleaning', name: 'Fence Cleaning', price: 300, category: 'Exterior' },
    { id: 'power-washing', name: 'Power Washing', price: 350, category: 'Exterior' },
    { id: 'pressure-washing', name: 'Pressure Washing', price: 400, category: 'Exterior' },
    { id: 'exterior-painting', name: 'Exterior Touch-Up Painting', price: 4800, category: 'Exterior' },
    { id: 'bush-trimming', name: 'Bush Trimming', price: 100, category: 'Landscape' },
    { id: 'weed-pulling', name: 'Weed Pulling', price: 125, category: 'Landscape' },
    { id: 'garden-maintenance', name: 'Garden Bed Maintenance', price: 175, category: 'Landscape' },
    { id: 'mulch-refresh', name: 'Mulch Refresh', price: 250, category: 'Landscape' },
    { id: 'landscape-lighting', name: 'Landscape Lighting', price: 1000, category: 'Landscape' },
    { id: 'solar-cleaning', name: 'Solar Panel Cleaning', price: 250, category: 'Specialty' },
    { id: 'spider-removal', name: 'Spider Web Removal', price: 100, category: 'Specialty' },
    { id: 'gutter-guards', name: 'Gutter Guard Installation', price: 500, category: 'Specialty' },
    { id: 'garage-cleanout', name: 'Garage Clean Out', price: 400, category: 'Specialty' },
    { id: 'diy-workshop', name: 'DIY Workshop', price: 300, category: 'Specialty' },
    { id: 'diy-coaching', name: 'DIY Coaching / On-Call Support', price: 25, category: 'Specialty' }
  ];

  const steps = [
    {
      title: 'Choose Plan',
      description: 'Select your membership tier'
    },
    {
      title: 'Add Services',
      description: 'Customize with add-ons'
    },
    {
      title: 'Your Info',
      description: 'Contact and property details'
    },
    {
      title: 'Payment',
      description: 'Secure checkout'
    }
  ];

  const getDiscountPercentage = (planId) => {
    switch (planId) {
      case 'standard': return 10;
      case 'premium': return 20;
      default: return 0;
    }
  };

  const calculateTotal = () => {
    if (!selectedPlan) return 0;
    
    const basePrices = {
      'basic': 65,
      'standard': 125,
      'premium': 225,
      'landlord': 79
    };
    
    const basePrice = basePrices[selectedPlan.id] || 0;
    
    const addOnTotal = selectedAddOns.reduce((total, addOn) => {
      const discount = getDiscountPercentage(selectedPlan.id) / 100;
      return total + (addOn.price * (1 - discount));
    }, 0);
    
    return basePrice + addOnTotal;
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setCurrentStep(1);
  };

  const handleAddOnToggle = (addOn) => {
    setSelectedAddOns(prev => {
      const exists = prev.find(item => item.id === addOn.id);
      if (exists) {
        return prev.filter(item => item.id !== addOn.id);
      } else {
        return [...prev, addOn];
      }
    });
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = (values) => {
    Modal.success({
      title: 'Welcome to HSP!',
      content: (
        <div>
          <p>Your membership has been successfully created!</p>
          <p><strong>Plan:</strong> {selectedPlan.name}</p>
          <p><strong>Monthly Total:</strong> ${calculateTotal().toFixed(2)}</p>
          <p>You'll receive a confirmation email shortly with your account details and first service scheduling information.</p>
        </div>
      ),
      onOk: () => {
        // Redirect to dashboard or home
        window.location.href = '/';
      }
    });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Title level={3} style={{ textAlign: 'center', marginBottom: '2rem' }}>
              Choose Your Membership Plan
            </Title>
            <Row gutter={[24, 24]}>
              {membershipPlans.map((plan) => (
                <Col xs={24} md={12} lg={6} key={plan.id}>
                  <Card
                    className={`pricing-card ${plan.popular ? 'featured' : ''}`}
                    style={{ 
                      height: '100%',
                      border: plan.special ? '2px solid #52c41a' : undefined,
                      cursor: 'pointer'
                    }}
                    bodyStyle={{ padding: '1.5rem' }}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {plan.popular && (
                      <div style={{ 
                        textAlign: 'center', 
                        marginBottom: '1rem',
                        background: '#1890ff',
                        color: 'white',
                        padding: '0.5rem',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}>
                        MOST POPULAR
                      </div>
                    )}
                    
                    {plan.special && (
                      <div style={{ 
                        textAlign: 'center', 
                        marginBottom: '1rem',
                        background: '#52c41a',
                        color: 'white',
                        padding: '0.5rem',
                        borderRadius: '20px',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}>
                        LANDLORD SPECIAL
                      </div>
                    )}
                    
                    <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                      <Title level={4} style={{ marginBottom: '0.5rem' }}>
                        {plan.name}
                      </Title>
                      <div style={{ marginBottom: '1rem' }}>
                        <Text style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1890ff' }}>
                          {plan.price}
                        </Text>
                        <Text style={{ fontSize: '1rem', color: '#666' }}>
                          {plan.period}
                        </Text>
                      </div>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                      {plan.features.map((feature, index) => (
                        <div key={index} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          marginBottom: '0.6rem' 
                        }}>
                          <CheckCircleOutlined style={{ 
                            color: '#52c41a', 
                            marginRight: '0.6rem',
                            fontSize: '1rem'
                          }} />
                          <Text style={{ fontSize: '0.9rem' }}>{feature}</Text>
                        </div>
                      ))}
                    </div>

                    <Button 
                      type={plan.popular ? 'primary' : 'default'}
                      size="large"
                      block
                      style={{ 
                        height: '40px',
                        borderRadius: '20px',
                        fontSize: '1rem',
                        fontWeight: '600'
                      }}
                    >
                      Choose {plan.name}
                    </Button>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        );

      case 1:
        return (
          <div>
            <Title level={3} style={{ textAlign: 'center', marginBottom: '2rem' }}>
              Add Optional Services
            </Title>
            <Alert
              message="Member Discount Applied"
              description={`You'll receive ${getDiscountPercentage(selectedPlan.id)}% off all add-on services with your ${selectedPlan.name} membership.`}
              type="info"
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: '2rem' }}
            />
            
            <Row gutter={[16, 16]}>
              {addOnServices.map((addOn) => {
                const isSelected = selectedAddOns.find(item => item.id === addOn.id);
                const discount = getDiscountPercentage(selectedPlan.id) / 100;
                const discountedPrice = addOn.price * (1 - discount);
                
                return (
                  <Col xs={24} sm={12} md={8} key={addOn.id}>
                    <Card
                      className="addon-card"
                      size="small"
                      bodyStyle={{ padding: '1rem' }}
                      style={{
                        border: isSelected ? '2px solid #1890ff' : undefined,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleAddOnToggle(addOn)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <Title level={5} style={{ margin: 0, fontSize: '1rem' }}>
                          {addOn.name}
                        </Title>
                        <div style={{ textAlign: 'right' }}>
                          {discount > 0 && (
                            <Text delete style={{ fontSize: '0.8rem', color: '#999' }}>
                              ${addOn.price}
                            </Text>
                          )}
                          <div>
                            <Tag color="blue" style={{ margin: 0 }}>
                              ${discountedPrice.toFixed(0)}
                            </Tag>
                          </div>
                        </div>
                      </div>
                      <Text type="secondary" style={{ fontSize: '0.9rem' }}>
                        {addOn.category} Service
                      </Text>
                      {isSelected && (
                        <CheckOutlined style={{ 
                          position: 'absolute', 
                          top: '8px', 
                          right: '8px', 
                          color: '#1890ff',
                          fontSize: '1.2rem'
                        }} />
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
            
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Button type="primary" size="large" onClick={handleNext}>
                Continue to Contact Info
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <Title level={3} style={{ textAlign: 'center', marginBottom: '2rem' }}>
              Your Information
            </Title>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleNext}
              style={{ maxWidth: '600px', margin: '0 auto' }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    rules={[{ required: true, message: 'Please enter your first name' }]}
                  >
                    <Input size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    rules={[{ required: true, message: 'Please enter your last name' }]}
                  >
                    <Input size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label="Email Address"
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label="Phone Number"
                    rules={[{ required: true, message: 'Please enter your phone number' }]}
                  >
                    <Input size="large" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="notes"
                label="Special Instructions (Optional)"
              >
                <TextArea rows={4} placeholder="Any specific requirements or notes for our team..." />
              </Form.Item>

              <Form.Item
                name="agreement"
                valuePropName="checked"
                rules={[{ required: true, message: 'Please accept the terms and conditions' }]}
              >
                <Checkbox>
                  I agree to the <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
                </Checkbox>
              </Form.Item>

              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <Space size="large">
                  <Button size="large" onClick={handlePrev}>
                    Back
                  </Button>
                  <Button type="primary" size="large" htmlType="submit">
                    Continue to Payment
                  </Button>
                </Space>
              </div>
            </Form>
          </div>
        );

      case 3:
        return (
          <div>
            <Title level={3} style={{ textAlign: 'center', marginBottom: '2rem' }}>
              Payment & Confirmation
            </Title>
            
            <Row gutter={[32, 32]}>
              <Col xs={24} lg={12}>
                <Card title="Order Summary" style={{ marginBottom: '2rem' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <Text strong>{selectedPlan.name}</Text>
                      <Text>{selectedPlan.price}{selectedPlan.period}</Text>
                    </div>
                    {selectedAddOns.map((addOn) => {
                      const discount = getDiscountPercentage(selectedPlan.id) / 100;
                      const discountedPrice = addOn.price * (1 - discount);
                      
                      return (
                        <div key={addOn.id} style={{ marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text>{addOn.name}</Text>
                            <Text>${discountedPrice.toFixed(0)}</Text>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Divider />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong>Monthly Total</Text>
                    <Text strong style={{ fontSize: '1.2rem', color: '#1890ff' }}>
                      ${calculateTotal().toFixed(2)}
                    </Text>
                  </div>
                </Card>
              </Col>
              
              <Col xs={24} lg={12}>
                <Form
                  layout="vertical"
                  onFinish={handleFinish}
                >
                  <Card title="Payment Information">
                    <Form.Item
                      name="cardNumber"
                      label="Card Number"
                      rules={[{ required: true, message: 'Please enter your card number' }]}
                    >
                      <Input size="large" placeholder="1234 5678 9012 3456" />
                    </Form.Item>

                    <Row gutter={16}>
                      <Col xs={12}>
                        <Form.Item
                          name="expiryDate"
                          label="Expiry Date"
                          rules={[{ required: true, message: 'Please enter expiry date' }]}
                        >
                          <Input size="large" placeholder="MM/YY" />
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item
                          name="cvv"
                          label="CVV"
                          rules={[{ required: true, message: 'Please enter CVV' }]}
                        >
                          <Input size="large" placeholder="123" />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      name="cardholderName"
                      label="Cardholder Name"
                      rules={[{ required: true, message: 'Please enter cardholder name' }]}
                    >
                      <Input size="large" />
                    </Form.Item>

                    <Form.Item
                      name="billingAddress"
                      label="Billing Address"
                      rules={[{ required: true, message: 'Please enter billing address' }]}
                    >
                      <Input size="large" />
                    </Form.Item>

                    <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                      <Space size="large">
                        <Button size="large" onClick={handlePrev}>
                          Back
                        </Button>
                        <Button type="primary" size="large" htmlType="submit" icon={<CreditCardOutlined />}>
                          Complete Payment
                        </Button>
                      </Space>
                    </div>
                  </Card>
                </Form>
              </Col>
            </Row>
          </div>
        );

      default:
        return null;
    }
  };

  // If user is not authenticated and there's a referral code, show auth modal
  if (!currentUser && searchParams.get('ref')) {
    return (
      <div style={{ padding: '40px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <Title level={1}>Welcome to HSP!</Title>
            <Paragraph style={{ fontSize: '1.2rem', color: '#666' }}>
              You've been referred by a friend! Sign in to get started with your membership.
            </Paragraph>
          </div>
        </div>
        
        <AuthModal 
          visible={authModalVisible} 
          onClose={() => setAuthModalVisible(false)}
          referralCode={searchParams.get('ref')}
        />
      </div>
    );
  }

  return (
    <>
      <Header />
      <div style={{ padding: '40px 0', marginTop: '80px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <Title level={1}>Start Your Membership</Title>
          <Paragraph style={{ fontSize: '1.2rem', color: '#666' }}>
            Join thousands of satisfied customers in just a few simple steps
          </Paragraph>
        </div>

        <Steps current={currentStep} items={steps} style={{ marginBottom: '3rem' }} />

        <Card style={{ minHeight: '500px' }}>
          {renderStepContent()}
        </Card>
      </div>
      
      <AuthModal 
        visible={authModalVisible} 
        onClose={() => setAuthModalVisible(false)}
        referralCode={searchParams.get('ref')}
      />
      </div>
    </>
  );
};

export default SignupPage;
